import * as React from "react";
import { useTranslation } from "react-i18next";
import { createFileRoute, useBlocker } from "@tanstack/react-router";
import WorkflowBuilder from "@/src/components/WorkflowBuilder.tsx";
import { useProjectContext } from "@/src/context/ProjectContext.tsx";
import { useAvatars } from "@/src/hooks/useAvatars.ts";
import { useCompanyLogos } from "@/src/hooks/useCompanyLogos.ts";
import { Workflow } from "@/src/types.ts";
import {
  useCreateWorkflow,
  useDeleteWorkflow,
  useListUserWorkflows,
  useUpdateWorkflow,
} from "@dataconnect/generated/react";
import { WorkflowNode, WorkflowEdge } from "../types";
import {
  diagnoseWorkflow,
  getMilieuFixedNodeIdsMissingStyle,
  hasMilieuFixedNodesMissingStyle,
} from "@/src/utils/workflowUtils";

const ProjectWorkflows: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { projectId } = Route.useParams();
  const {
    vibes,
    scenes,
    highlightsConfigs,
    isProjectDataLoaded,
    savedColorSwatches,
    lifestylePresets,
    productLightsOn,
    productLightsWarmth,
    createLifestylePreset: handleCreateLifestylePreset,
    deleteLifestylePreset: handleDeleteLifestylePreset,
  } = useProjectContext();
  const { savedAvatars } = useAvatars();
  const { savedLogos } = useCompanyLogos();
  const { mutateAsync: createWorkflow } = useCreateWorkflow();
  const { mutateAsync: updateWorkflow } = useUpdateWorkflow();
  const { mutateAsync: deleteWorkflow } = useDeleteWorkflow();
  const { data: workflowsData, refetch: refetchWorkflows } =
    useListUserWorkflows();

  const serverWorkflows: Workflow[] = React.useMemo(() => {
    if (!workflowsData?.workflows) return [];
    return workflowsData.workflows.map((workflow) => {
      const settings = (workflow.settings || {}) as {
        nodes?: WorkflowNode[];
        edges?: WorkflowEdge[];
        createdAt?: number;
      };
      return {
        id: workflow.id,
        name: workflow.name,
        nodes: settings.nodes || [],
        edges: settings.edges || [],
        createdAt: settings.createdAt ?? 0,
        lastEditedAt: workflow.lastEditedAt ?? undefined,
        lastUsedAt: workflow.lastUsedAt ?? undefined,
      };
    });
  }, [workflowsData]);

  const [isSaving, setIsSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [selectedWorkflowId, setSelectedWorkflowId] = React.useState<
    string | null
  >(null);
  const [draftWorkflow, setDraftWorkflow] = React.useState<Workflow | null>(
    null
  );
  const [lastSavedSnapshot, setLastSavedSnapshot] = React.useState<
    string | null
  >(null);
  const [showSwitchWarning, setShowSwitchWarning] = React.useState(false);
  const [pendingSwitchId, setPendingSwitchId] = React.useState<string | null>(
    null
  );
  const [showSaveWarning, setShowSaveWarning] = React.useState(false);
  const [isDeletingWorkflow, setIsDeletingWorkflow] = React.useState(false);

  React.useEffect(() => {
    if (selectedWorkflowId) {
      const found = serverWorkflows.find((w) => w.id === selectedWorkflowId);
      if (found) {
        setDraftWorkflow((prev) => {
          if (prev && prev.id === selectedWorkflowId) return prev;
          return found;
        });

        // If we don't have a snapshot for the current selection, or it's a new selection,
        // set the baseline from the server data.
        setLastSavedSnapshot((prevSnapshot) => {
          if (prevSnapshot) {
            try {
              const parsed = JSON.parse(prevSnapshot);
              if (parsed.id === selectedWorkflowId) return prevSnapshot;
            } catch (e) {}
          }
          return JSON.stringify(found);
        });
      }
    } else {
      if (selectedWorkflowId === null) {
        setDraftWorkflow(null);
        setLastSavedSnapshot(null);
      }
    }
  }, [selectedWorkflowId, serverWorkflows]);

  // Auto-select first workflow when opening the tab and list has workflows
  React.useEffect(() => {
    if (
      !isDeletingWorkflow &&
      serverWorkflows.length > 0 &&
      selectedWorkflowId === null
    ) {
      setSelectedWorkflowId(serverWorkflows[0].id);
    }
  }, [serverWorkflows, selectedWorkflowId, isDeletingWorkflow]);

  const handleSaveWorkflow = (workflow: Workflow) => {
    const trimmed = workflow.name.trim();
    const previousName =
      draftWorkflow?.id === workflow.id
        ? draftWorkflow.name.trim()
        : serverWorkflows.find((w) => w.id === workflow.id)?.name.trim();
    const name = trimmed || previousName || "";
    if (!name) {
      setSaveError(t("studio.workflowNameRequired"));
      return;
    }
    setSaveError(null);
    setDraftWorkflow({ ...workflow, name });
    if (workflow.id !== selectedWorkflowId) {
      setSelectedWorkflowId(workflow.id);
    }
  };

  const handleSelectWorkflow = (id: string | null) => {
    if (id === selectedWorkflowId) return;
    if (isDirty) {
      if (hasAffectedSteps) {
        setPendingSwitchId(id);
        setShowSwitchWarning(true);
      } else if (window.confirm(t("studio.confirmLeaveWorkflow"))) {
        setSelectedWorkflowId(id);
      }
    } else {
      setSelectedWorkflowId(id);
    }
  };

  const handleDeleteWorkflow = async (id: string) => {
    if (confirm(t("studio.confirmDeleteWorkflow"))) {
      setIsDeletingWorkflow(true);
      try {
        await deleteWorkflow({ id });

        if (selectedWorkflowId === id) {
          setSelectedWorkflowId(null);
          setDraftWorkflow(null);
          setLastSavedSnapshot(null);
        }
        await refetchWorkflows();
      } catch (error) {
        console.error("Failed to delete workflow:", error);
        setSaveError(t("studio.errorDeleteWorkflow"));
      } finally {
        setIsDeletingWorkflow(false);
      }
    }
  };

  const displayWorkflows = React.useMemo(() => {
    if (!draftWorkflow) return serverWorkflows;
    const index = serverWorkflows.findIndex((w) => w.id === draftWorkflow.id);
    if (index >= 0) {
      const copy = [...serverWorkflows];
      copy[index] = draftWorkflow;
      return copy;
    }
    return [...serverWorkflows, draftWorkflow];
  }, [serverWorkflows, draftWorkflow]);

  const isDirty = React.useMemo(() => {
    if (!draftWorkflow) return false;
    const currentSnapshot = JSON.stringify(draftWorkflow);
    return currentSnapshot !== lastSavedSnapshot;
  }, [draftWorkflow, lastSavedSnapshot]);

  const hasMissingMilieuFixedStyle = React.useMemo(
    () => hasMilieuFixedNodesMissingStyle(draftWorkflow),
    [draftWorkflow]
  );
  const missingMilieuFixedStyleMessage = t(
    "studio.workflowInpaintStyleRequiredError",
    {
      count: getMilieuFixedNodeIdsMissingStyle(draftWorkflow).length,
      defaultValue:
        "Milieu Fixed requires a room style. Select or upload a style before saving this workflow.",
    }
  );
  // Steps that will not run as their card suggests. The builder marks them
  // on the canvas; here they gate save/leave/switch and feed the header count.
  // Until the project row is in, the template list is empty for a reason that
  // has nothing to do with the workflow, so template checks wait for it.
  const affectedStepCount = React.useMemo(
    () =>
      new Set(
        diagnoseWorkflow(draftWorkflow, {
          highlightsConfigs: isProjectDataLoaded
            ? highlightsConfigs
            : undefined,
        }).map((d) => d.nodeId)
      ).size,
    [draftWorkflow, highlightsConfigs, isProjectDataLoaded]
  );
  const hasAffectedSteps = affectedStepCount > 0;

  const leaveBlockerResolver = useBlocker({
    shouldBlockFn: (_args) => isDirty,
    withResolver: true,
  });

  const handlePersistWorkflow = async (force?: boolean) => {
    if (!draftWorkflow) return false;
    if (!draftWorkflow.name.trim()) {
      setSaveError(t("studio.workflowNameRequired"));
      return false;
    }
    if (hasMissingMilieuFixedStyle) {
      setSaveError(missingMilieuFixedStyleMessage);
      return false;
    }
    if (!force && hasAffectedSteps) {
      setShowSaveWarning(true);
      return false;
    }
    setIsSaving(true);
    setSaveError(null);
    try {
      const payload = {
        workflowId: draftWorkflow.id,
        name: draftWorkflow.name,
        settings: {
          nodes: draftWorkflow.nodes,
          edges: draftWorkflow.edges,
          createdAt: draftWorkflow.createdAt,
        },
      };

      const exists = serverWorkflows.some((w) => w.id === draftWorkflow.id);
      let finalWorkflow = draftWorkflow;

      if (exists) {
        await updateWorkflow(payload);
      } else {
        const response = await createWorkflow(payload);
        if (response.workflow_insert?.id) {
          const newId = response.workflow_insert.id;
          if (newId !== draftWorkflow.id) {
            finalWorkflow = { ...draftWorkflow, id: newId };
            setDraftWorkflow(finalWorkflow);
            setSelectedWorkflowId(newId);
          }
        }
      }

      // Update baseline immediately to disable save button
      setLastSavedSnapshot(JSON.stringify(finalWorkflow));
      await refetchWorkflows();
      return true;
    } catch (error) {
      console.error("Failed to save workflows:", error);
      setSaveError(t("studio.errorSaveWorkflow"));
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex-grow h-full bg-gray-50/50 flex flex-col relative overflow-hidden">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-3">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl tracking-tight font-semibold text-gray-900">
                Workflows
              </h1>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-[11px] font-medium text-blue-600">
                <svg
                  className="w-3 h-3"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5a17.92 17.92 0 01-8.716-2.247m0 0A8.966 8.966 0 013 12c0-1.528.38-2.968 1.05-4.228"
                  />
                </svg>
                {t("studio.workflowsGlobalHint")}
              </span>
            </div>
            <p className="text-xs text-gray-400 mt-0.5">
              {displayWorkflows.length}{" "}
              {displayWorkflows.length === 1 ? "workflow" : "workflows"}
            </p>
            {!saveError && hasMissingMilieuFixedStyle ? (
              <p className="text-xs text-amber-700 mt-1">
                {missingMilieuFixedStyleMessage}
              </p>
            ) : !saveError && hasAffectedSteps ? (
              <p className="text-xs text-amber-700 mt-1">
                {t("studio.workflowDiagnosticsSummary", {
                  count: affectedStepCount,
                })}
              </p>
            ) : null}
            {saveError && (
              <p className="text-xs text-red-600 mt-1">{saveError}</p>
            )}
          </div>
        </div>
        <button
          onClick={() => handlePersistWorkflow()}
          className="px-4 py-2 bg-black text-white text-sm font-semibold rounded-lg hover:bg-gray-800 transition disabled:opacity-60 disabled:cursor-not-allowed"
          disabled={
            isSaving ||
            !draftWorkflow ||
            !isDirty ||
            !draftWorkflow.name.trim() ||
            hasMissingMilieuFixedStyle
          }
        >
          {isSaving
            ? t("studio.saving")
            : isDirty
              ? t("studio.saveWorkflow")
              : t("studio.saved", { defaultValue: "Saved" })}
        </button>
      </div>
      <WorkflowBuilder
        workflows={displayWorkflows}
        onSaveWorkflow={handleSaveWorkflow}
        onDeleteWorkflow={handleDeleteWorkflow}
        vibes={vibes}
        scenes={scenes}
        highlightsConfigs={highlightsConfigs}
        projectDataLoaded={isProjectDataLoaded}
        savedColorSwatches={savedColorSwatches}
        savedAvatars={savedAvatars}
        savedLogos={savedLogos}
        lifestylePresets={lifestylePresets}
        studioProductLightsOn={productLightsOn}
        studioProductLightsWarmth={productLightsWarmth}
        onCreateLifestylePreset={handleCreateLifestylePreset}
        onDeleteLifestylePreset={handleDeleteLifestylePreset}
        onSelectWorkflow={handleSelectWorkflow}
        selectedWorkflowId={selectedWorkflowId}
      />

      {/* Leave (navigation) warning — unconnected or unsaved */}
      {leaveBlockerResolver.status === "blocked" &&
        leaveBlockerResolver.reset &&
        leaveBlockerResolver.proceed && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
            role="dialog"
            aria-modal="true"
            aria-labelledby="leave-workflow-title"
          >
            <div className="bg-white rounded-xl shadow-xl max-w-lg mx-4 p-6">
              <h2
                id="leave-workflow-title"
                className="text-lg font-semibold text-gray-900 mb-6"
              >
                {hasAffectedSteps
                  ? t("studio.workflowUnconnectedWarning")
                  : t("studio.confirmLeaveWorkflow")}
              </h2>
              <div className="flex flex-nowrap justify-end items-center gap-3">
                <button
                  type="button"
                  onClick={() => leaveBlockerResolver.reset?.()}
                  disabled={isSaving}
                  className="flex-shrink-0 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {t("studio.workflowUnconnectedStay")}
                </button>
                <button
                  type="button"
                  onClick={() => leaveBlockerResolver.proceed?.()}
                  disabled={isSaving}
                  className="flex-shrink-0 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {i18n.language?.startsWith("de")
                    ? t("studio.workflowLeave", { defaultValue: "Verlassen" })
                    : t("studio.workflowLeave", { defaultValue: "Leave" })}
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    const useForce = hasAffectedSteps;
                    const didSave = await handlePersistWorkflow(useForce);
                    if (didSave) {
                      leaveBlockerResolver.proceed?.();
                    }
                  }}
                  disabled={isSaving}
                  className="flex-shrink-0 px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSaving
                    ? t("studio.saving")
                    : i18n.language?.startsWith("de")
                      ? t("studio.workflowSaveAndLeave", {
                          defaultValue: "Speichern und verlassen",
                        })
                      : t("studio.workflowSaveAndLeave", {
                          defaultValue: "Save and leave",
                        })}
                </button>
              </div>
            </div>
          </div>
        )}

      {/* Switch workflow warning */}
      {showSwitchWarning && pendingSwitchId !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="switch-workflow-title"
        >
          <div className="bg-white rounded-xl shadow-xl max-w-md mx-4 p-6">
            <h2
              id="switch-workflow-title"
              className="text-lg font-semibold text-gray-900 mb-6"
            >
              {t("studio.workflowUnconnectedWarning")}
            </h2>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowSwitchWarning(false);
                  setPendingSwitchId(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm font-medium"
              >
                {t("studio.workflowUnconnectedStay")}
              </button>
              <button
                type="button"
                onClick={() => {
                  setSelectedWorkflowId(pendingSwitchId);
                  setShowSwitchWarning(false);
                  setPendingSwitchId(null);
                }}
                className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 text-sm font-medium"
              >
                {t("studio.workflowUnconnectedSwitchAnyway")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save workflow warning */}
      {showSaveWarning && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="save-workflow-title"
        >
          <div className="bg-white rounded-xl shadow-xl max-w-md mx-4 p-6">
            <h2
              id="save-workflow-title"
              className="text-lg font-semibold text-gray-900 mb-6"
            >
              {t("studio.workflowUnconnectedWarning")}
            </h2>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowSaveWarning(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm font-medium"
              >
                {t("studio.workflowUnconnectedGoBack")}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowSaveWarning(false);
                  handlePersistWorkflow(true);
                }}
                className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 text-sm font-medium"
              >
                {t("studio.workflowUnconnectedSaveAnyway")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const Route = createFileRoute("/projects/$projectId/workflows")({
  component: ProjectWorkflows,
});
